import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { User } from '@/core/api/entities/user'
import { SELECTED_ACCOUNT_ID_KEY } from '@/core/store/slices/selectedAccountSlice'

interface AuthState {
	user: User | null
	token: string | null
	isAuthenticated: boolean
	isLoading: boolean
	error: string | null
}

const initialState: AuthState = {
	user: null,
	token: null,
	isAuthenticated: false,
	isLoading: false,
	error: null,
}

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		loginStart: (state) => {
			state.isLoading = true
			state.error = null
		},
		loginSuccess: (
			state,
			action: PayloadAction<{
				user: User
				token: string
			}>
		) => {
			state.isLoading = false
			state.isAuthenticated = true
			state.user = action.payload.user
			state.token = action.payload.token
			state.error = null

			// Store auth data in localStorage for persistence
			localStorage.setItem('auth_token', action.payload.token)
			// Clear any previously selected account when a new session begins
			localStorage.removeItem(SELECTED_ACCOUNT_ID_KEY)
		},
		loginFailure: (state, action: PayloadAction<string>) => {
			state.isLoading = false
			state.error = action.payload
			state.isAuthenticated = false
			state.user = null
			state.token = null

			// Clear any stored auth data
			localStorage.removeItem('auth_token')
		},
		logout: (state) => {
			state.user = null
			state.token = null
			state.isAuthenticated = false

			// Clear stored auth data
			localStorage.removeItem('auth_token')
			localStorage.removeItem(SELECTED_ACCOUNT_ID_KEY)
		},
		updateUser: (state, action: PayloadAction<User>) => {
			state.user = action.payload
		},
		// Restore auth state from localStorage (on app init)
		restoreAuthState: (state) => {
			const token = localStorage.getItem('auth_token')
			if (token) {
				state.token = token
				state.isAuthenticated = true
			}
		},
		// Set user data after fetching (used during initialization)
		setUser: (state, action: PayloadAction<User>) => {
			state.user = action.payload
		},
	},
})

export const {
	loginStart,
	loginSuccess,
	loginFailure,
	logout,
	updateUser,
	restoreAuthState,
	setUser,
} = authSlice.actions

export const authReducer = authSlice.reducer
